import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { ADMIN_ADDRESSES } from "@cede/utils";
import { DEBUG_OVERRIDE_ADDRESS_KEY } from "./useDebugOverrideAddress";
import { useAuthenticationProvider } from "@/hooks/authentication/useAuthenticationProvider";

const EXPIRY_KEY = "cede-override-address-expiry";
const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

const listeners = new Set<() => void>();

function emitChange() {
  listeners.forEach((l) => l());
}

function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

function getOverrideAddress() {
  return localStorage.getItem(DEBUG_OVERRIDE_ADDRESS_KEY);
}

function clearImpersonation() {
  localStorage.removeItem(DEBUG_OVERRIDE_ADDRESS_KEY);
  localStorage.removeItem(EXPIRY_KEY);
  emitChange();
}

/**
 * Hook to detect if a debug override address is set in localStorage.
 *
 * Automatically sets a 5-minute TTL when an override is first detected.
 * Shows a countdown and auto-clears + reloads when the timer expires.
 */
export const useIsImpersonating = () => {
  const { user } = useAuthenticationProvider();
  const overrideAddress = useSyncExternalStore(subscribe, getOverrideAddress, () => null);
  const [remainingSeconds, setRemainingSeconds] = useState<number>(0);

  const realAddress = user?.wallet?.address;
  const isAdmin = !!realAddress && ADMIN_ADDRESSES.some((a) => a.toLowerCase() === realAddress.toLowerCase());
  const isActive = isAdmin && !!overrideAddress;

  // Set expiry when override is first detected
  useEffect(() => {
    if (!isActive) {
      setRemainingSeconds(0);
      return;
    }

    const existingExpiry = localStorage.getItem(EXPIRY_KEY);
    if (!existingExpiry) {
      localStorage.setItem(EXPIRY_KEY, String(Date.now() + DEFAULT_TTL_MS));
    }

    const tick = () => {
      const expiry = Number(localStorage.getItem(EXPIRY_KEY));
      const diff = Math.max(0, Math.ceil((expiry - Date.now()) / 1000));

      if (diff <= 0) {
        clearImpersonation();
        window.location.reload();
        return;
      }

      setRemainingSeconds(diff);
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [isActive]);

  const stopImpersonating = useCallback(() => {
    clearImpersonation();
    window.location.reload();
  }, []);

  return {
    isImpersonating: isActive,
    impersonatedAddress: overrideAddress,
    remainingSeconds,
    stopImpersonating,
  };
};
